import { useCallback, useEffect, useState } from 'react';
import { LicenseStatus } from '../types/manageLicenses';
import { DEFAULT_LICENSE_FILTERS } from '../types/manageLicenses';
import type { LicenseFilterState } from '../types/manageLicenses';

const KEY = 'learnleaf.manage-licenses.views.v1';
const MAX_VIEWS = 20;

export interface SavedView {
  id: string;
  name: string;
  system?: boolean;
  createdAt?: string;
  filters: LicenseFilterState;
}

interface SavedViewsStore {
  version: 1;
  views: SavedView[];
}

const SYSTEM_VIEWS: SavedView[] = [
  {
    id: 'system:expiring-30d',
    name: 'Expiring in 30 days',
    system: true,
    filters: { ...DEFAULT_LICENSE_FILTERS, status: LicenseStatus.Active, expiringWithinDays: 30 },
  },
  {
    id: 'system:at-or-over-limit',
    name: 'At or over device limit',
    system: true,
    filters: {
      ...DEFAULT_LICENSE_FILTERS,
      status: LicenseStatus.Active,
      atOrOverDeviceLimit: true,
      sort: 'activeDeviceCount',
      dir: 'desc',
    },
  },
];

function read(): SavedViewsStore {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { version: 1, views: [] };
    const parsed = JSON.parse(raw) as Partial<SavedViewsStore>;
    if (parsed.version !== 1 || !Array.isArray(parsed.views)) {
      return { version: 1, views: [] };
    }
    return { version: 1, views: parsed.views };
  } catch {
    return { version: 1, views: [] };
  }
}

function write(store: SavedViewsStore) {
  localStorage.setItem(KEY, JSON.stringify(store));
}

function makeId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `v_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function useLicenseSavedViews() {
  const [userViews, setUserViews] = useState<SavedView[]>(() => read().views);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY) setUserViews(read().views);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // System views are always prepended; they are not persisted
  const views: SavedView[] = [...SYSTEM_VIEWS, ...userViews];

  const save = useCallback((name: string, filters: LicenseFilterState): SavedView | null => {
    if (userViews.length >= MAX_VIEWS) return null;
    const view: SavedView = {
      id: makeId(),
      name,
      createdAt: new Date().toISOString(),
      filters,
    };
    const next = [...userViews, view];
    write({ version: 1, views: next });
    setUserViews(next);
    return view;
  }, [userViews]);

  const rename = useCallback((id: string, name: string) => {
    // System views cannot be renamed
    const next = userViews.map(v => (v.id === id ? { ...v, name } : v));
    write({ version: 1, views: next });
    setUserViews(next);
  }, [userViews]);

  const remove = useCallback((id: string) => {
    // System views cannot be removed
    const next = userViews.filter(v => v.id !== id);
    write({ version: 1, views: next });
    setUserViews(next);
  }, [userViews]);

  return { views, save, rename, remove, max: MAX_VIEWS };
}
